/**
 * SPEC-web-planner-013 AC-65 (R87): which invite roles a plan offers. Mirrors
 * users-api PersonaPlans (User.Api/appsettings.json, SeatGuard.cs): Entry [is, admin],
 * Mid adds fs, Top adds cro. A client with no plan (legacy) or an unknown plan string
 * gets every role: the server decides (SeatGuard treats no plan as Top).
 */
export type InviteRole = 'is' | 'fs' | 'cro' | 'admin';

export const PERSONA_PLANS: Readonly<Record<string, ReadonlyArray<InviteRole>>> = {
    entry: ['is', 'admin'],
    mid: ['is', 'admin', 'fs'],
    top: ['is', 'admin', 'fs', 'cro'],
};

/** The lowest plan that offers each role (for the "Available on …" note). */
const FIRST_PLAN: Readonly<Record<InviteRole, string>> = { is: 'Entry', admin: 'Entry', fs: 'Mid', cro: 'Top' };

export interface RoleAvailability {
    role: InviteRole;
    available: boolean;
    /** "Available on Mid" / "Available on Top" when not available. */
    note: string | null;
}

/** The roles of a plan; null when the plan is unknown (every role then stays available). */
export function rolesForPlan(plan: string | null | undefined): ReadonlyArray<InviteRole> | null {
    const key = String(plan ?? '').trim().toLowerCase();
    return PERSONA_PLANS[key] ?? null;
}

/**
 * SPEC-web-planner-026: whether a plan offers the CRO pages, as PersonaGuard decides it. A missing (undefined) plan is
 * read as null; a null or unknown plan offers every role (SeatGuard treats it as Top).
 */
export function planOffersCro(plan: string | null | undefined): boolean {
    const roles = rolesForPlan(plan ?? null);
    return roles === null || roles.includes('cro');
}

export function roleAvailability(plan: string | null | undefined, role: InviteRole): RoleAvailability {
    const roles = rolesForPlan(plan);
    const available = !roles || roles.includes(role);
    return { role, available, note: available ? null : `Available on ${FIRST_PLAN[role]}` };
}

/** The first role, in the given order, that the plan offers. */
export function defaultRole(plan: string | null | undefined, order: ReadonlyArray<InviteRole>): InviteRole {
    return order.find((r) => roleAvailability(plan, r).available) ?? order[0];
}
